import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Loader2 } from "lucide-react";
import { reversePlace } from "@/lib/geo";

const makeIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
    iconSize: [18, 18], iconAnchor: [9, 18],
  });
const originIcon = makeIcon("#2563EB");
const destIcon = makeIcon("#10B981");

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 12);
    else if (points.length >= 2) map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

function ClickCapture({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
}

export function MapPicker({ origin, destination, onPick, originLabel = "Pickup", destinationLabel = "Destination", height = 260 }) {
  const [mode, setMode] = useState("origin");
  const [busy, setBusy] = useState(false);
  const [routeCoords, setRouteCoords] = useState(null);
  const hasBoth = origin?.lat != null && destination?.lat != null;

  useEffect(() => {
    if (!hasBoth) { setRouteCoords(null); return; }
    let active = true;
    (async () => {
      try {
        const r = await fetch(`https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`);
        const d = await r.json();
        if (active && d.routes?.[0]) setRouteCoords(d.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]));
        else if (active) setRouteCoords(null);
      } catch { if (active) setRouteCoords(null); }
    })();
    return () => { active = false; };
  }, [hasBoth, origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

  const handleClick = async (latlng) => {
    setBusy(true);
    let address = `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`;
    try {
      const name = await reversePlace(latlng.lat, latlng.lng);
      if (name) address = name;
    } catch { /* keep coord fallback */ }
    onPick(mode, { lat: latlng.lat, lng: latlng.lng, address });
    setMode((m) => (m === "origin" ? "destination" : m));
    setBusy(false);
  };

  const points = [];
  if (origin?.lat != null) points.push([origin.lat, origin.lng]);
  if (destination?.lat != null) points.push([destination.lat, destination.lng]);
  const center = points[0] || [22.3511, 78.6677];
  const boundsPoints = routeCoords?.length ? routeCoords : points;

  return (
    <div data-testid="map-picker">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs text-slate-500">Tap the map to drop a pin for:</span>
        <button type="button" onClick={() => setMode("origin")} data-testid="mappick-origin" className={`text-xs font-medium px-3 py-1 rounded-full border transition-colors ${mode === "origin" ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{originLabel}</button>
        <button type="button" onClick={() => setMode("destination")} data-testid="mappick-dest" className={`text-xs font-medium px-3 py-1 rounded-full border transition-colors ${mode === "destination" ? "bg-emerald-500 text-white border-emerald-500" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{destinationLabel}</button>
        {busy && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />}
      </div>
      <div style={{ height }} className="overflow-hidden rounded-2xl border border-slate-200">
        <MapContainer center={center} zoom={points.length ? 6 : 5} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickCapture onClick={handleClick} />
          {origin?.lat != null && <Marker position={[origin.lat, origin.lng]} icon={originIcon}><Popup>{originLabel}</Popup></Marker>}
          {destination?.lat != null && <Marker position={[destination.lat, destination.lng]} icon={destIcon}><Popup>{destinationLabel}</Popup></Marker>}
          {routeCoords ? <Polyline positions={routeCoords} pathOptions={{ color: "#2563EB", weight: 4 }} /> : (hasBoth && <Polyline positions={points} pathOptions={{ color: "#2563EB", weight: 3, dashArray: "6 8" }} />)}
          <FitBounds points={boundsPoints} />
        </MapContainer>
      </div>
    </div>
  );
}
